// frontend/src/api/client.js

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and haven't retried yet, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken
          });
          
          const { accessToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        // Redirect to the correct login page based on current route
        const isMobile = window.location.pathname.startsWith('/mobile');
        window.location.href = isMobile ? '/mobile/login' : '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => apiClient.post('/auth/login', credentials),
  logout: () => apiClient.post('/auth/logout'),
  me: () => apiClient.get('/auth/me'),
  refreshToken: (refreshToken) => apiClient.post('/auth/refresh', { refreshToken }),
  changePassword: (data) => apiClient.post('/auth/change-password', data),
};

// ordersAPI 
export const texpactAPI = {
  getPartners: () => apiClient.get('/texpact/partners'),
  getByPartner: (partnerId, queryString = '') => {
    const url = queryString
      ? `/texpact/partner/${partnerId}?${queryString}`
      : `/texpact/partner/${partnerId}`;
    return apiClient.get(url);
  },
  getDetails:    (poCode)               => apiClient.get(`/texpact/${poCode}`),
  getByPublicId: (publicId)             => apiClient.get(`/texpact/view/${publicId}`),
  getMatrix:     (poCode, productCode)  => apiClient.get(`/texpact/${poCode}/matrix/${productCode}`),
  getNavReceivedQuantities: (poCode)    => apiClient.post('/texpact/nav-received-quantities', { poCode }),
};

export const shipmentsAPI = {
  create: (data) => apiClient.post('/shipments', data),
  getByOrder: (poCode) => apiClient.get(`/shipments/order/${poCode}`),
  getAll: (queryString = '') => {
    const url = queryString ? `/shipments/list?${queryString}` : '/shipments/list';
    return apiClient.get(url);
  },
  resend: (shipmentCode) => apiClient.post(`/shipments/${shipmentCode}/resend`),

  // Get all messages_header associated with a shipment (by shipments_header_id)
  getMessages: (shipmentId) => apiClient.get(`/shipments/${shipmentId}/messages`),
 
  // Get all messages_header with filters + pagination (for the standalone MessageList page)
  getAllMessages: (queryString) => apiClient.get(`/shipments/messages?${queryString}`),
};


export const measurementAPI = {
  // All IDs are public_ids (UUIDs) — never internal bigint IDs
  getHeaders:       (params)                  => apiClient.get('/qual/measurements', { params }),
  getHeaderDetail:  (headerPublicId)          => apiClient.get(`/qual/measurements/${headerPublicId}`),
  getSessions:      (headerPublicId)          => apiClient.get(`/qual/measurements/${headerPublicId}/sessions`),
  createSession:    (headerPublicId, data)    => apiClient.post(`/qual/measurements/${headerPublicId}/sessions`, data),
  getResults:       (sessionPublicId)         => apiClient.get(`/qual/measurements/sessions/${sessionPublicId}/results`),
  saveResults:      (sessionPublicId, data)   => apiClient.post(`/qual/measurements/sessions/${sessionPublicId}/results`, data),  // data: { results, component_code, sample_no }
  getHorzGroups:    (companyId)               => apiClient.get('/qual/measurements/horz-groups', { params: { companyId } }),
  getHorzValues:    (groupPublicId)           => apiClient.get(`/qual/measurements/horz-groups/${groupPublicId}/values`),
};

export const menuAPI = {
  getAll: (languageCode = null) => {
    const url = languageCode ? `/menu?lang=${languageCode}` : '/menu';
    return apiClient.get(url);
  },
  checkPermission: (menuKey) => apiClient.get(`/menu/permission/${menuKey}`),
};

export const usersAPI = {
  getAll: (queryString = '') => {
    const url = queryString ? `/users?${queryString}` : '/users';
    return apiClient.get(url);
  },
  getById: (userId) => apiClient.get(`/users/${userId}`),
  create: (data) => apiClient.post('/users', data),
  update: (userId, data) => apiClient.put(`/users/${userId}`, data),
  resetPassword: (userId, data) => apiClient.post(`/users/${userId}/reset-password`, data),
  delete: (userId) => apiClient.delete(`/users/${userId}`),
};

export const groupsAPI = {
  getAll: (queryString = '') => {
    const url = queryString ? `/groups?${queryString}` : '/groups';
    return apiClient.get(url);
  },
  getById: (groupId) => apiClient.get(`/groups/${groupId}`),
  create: (data) => apiClient.post('/groups', data),
  update: (groupId, data) => apiClient.put(`/groups/${groupId}`, data),
  delete: (groupId) => apiClient.delete(`/groups/${groupId}`),
};

export const permissionsAPI = {
  getMenuItems:           ()                         => apiClient.get('/permissions/menu-items'),
  getGroupPermissions:    (groupId)                  => apiClient.get(`/permissions/group/${groupId}`),
  updateGroupPermissions: (groupId, permissions)     => apiClient.put(`/permissions/group/${groupId}`, { permissions }),
  updatePermission:       (groupId, menuKey, hasPerm)=> apiClient.put(`/permissions/group/${groupId}/menu/${menuKey}`, { hasPermission: hasPerm }),
  // Current user's effective permissions (union of all groups)
  getMyPermissions:       ()                         => apiClient.get('/permissions/my'),
};

export const divisionsAPI = {
  getAll: (queryString = '') => {
    const url = queryString ? `/divisions?${queryString}` : '/divisions';
    return apiClient.get(url);
  },
  getById: (divisionId) => apiClient.get(`/divisions/${divisionId}`),
  create: (data) => apiClient.post('/divisions', data),
  update: (divisionId, data) => apiClient.put(`/divisions/${divisionId}`, data),
  delete: (divisionId) => apiClient.delete(`/divisions/${divisionId}`),
};

export const productionTypesAPI = {
  getAll: (queryString = '') => {
    const url = queryString ? `/production-types?${queryString}` : '/production-types';
    return apiClient.get(url);
  },
  getById: (productionTypeId) => apiClient.get(`/production-types/${productionTypeId}`),
  create: (data) => apiClient.post('/production-types', data),
  update: (productionTypeId, data) => apiClient.put(`/production-types/${productionTypeId}`, data),
  delete: (productionTypeId) => apiClient.delete(`/production-types/${productionTypeId}`),
};

export const sampleOrderTypesAPI = {
  getAll: (queryString = '') => {
    const url = queryString ? `/sample-order-types?${queryString}` : '/sample-order-types';
    return apiClient.get(url);
  },
  getById: (sampleOrderTypeId) => apiClient.get(`/sample-order-types/${sampleOrderTypeId}`),
  create: (data) => apiClient.post('/sample-order-types', data),
  update: (sampleOrderTypeId, data) => apiClient.put(`/sample-order-types/${sampleOrderTypeId}`, data),
  delete: (sampleOrderTypeId) => apiClient.delete(`/sample-order-types/${sampleOrderTypeId}`),
};

export const translationsAPI = {
  getLanguages: () => apiClient.get('/translations/languages'),
  getTranslations: (languageCode, page = null) => {
    const url = page ? `/translations/${languageCode}?page=${page}` : `/translations/${languageCode}`;
    return apiClient.get(url);
  },
  updateUserLanguage: (languageCode) => apiClient.post('/translations/user/language', { languageCode }),
};


export const customersAPI = {
  getAll: (queryString = '') => {
    const url = queryString ? `/customers?${queryString}` : '/customers';
    return apiClient.get(url);
  },
  getById: (customerId) => apiClient.get(`/customers/${customerId}`),
  create: (data) => apiClient.post('/customers', data),
  update: (customerId, data) => apiClient.put(`/customers/${customerId}`, data),
  delete: (customerId) => apiClient.delete(`/customers/${customerId}`),
};

export const collectionsAPI = {
  getAll: (queryString = '') => {
    const url = queryString ? `/collections?${queryString}` : '/collections';
    return apiClient.get(url);
  },
  getById: (collectionId) => apiClient.get(`/collections/${collectionId}`),
  create: (data) => apiClient.post('/collections', data),
  update: (collectionId, data) => apiClient.put(`/collections/${collectionId}`, data),
  delete: (collectionId) => apiClient.delete(`/collections/${collectionId}`),
};

export const numberSeriesAPI = {
  getAll: () => apiClient.get('/number-series'),
  getById: (seriesId) => apiClient.get(`/number-series/${seriesId}`),
  create: (data) => apiClient.post('/number-series', data),
  update: (seriesId, data) => apiClient.put(`/number-series/${seriesId}`, data),
  delete: (seriesId) => apiClient.delete(`/number-series/${seriesId}`),
  getNext: (seriesCode) => apiClient.post(`/number-series/next/${seriesCode}`),
};

export const horzGroupsAPI = {
  getAll: () => apiClient.get('/horz-groups'),
  getById: (groupId) => apiClient.get(`/horz-groups/${groupId}`),
  create: (data) => apiClient.post('/horz-groups', data),
  update: (groupId, data) => apiClient.put(`/horz-groups/${groupId}`, data),
  delete: (groupId) => apiClient.delete(`/horz-groups/${groupId}`),
};

export const horzValuesAPI = {
  getAll: (groupCode = '') => {
    const url = groupCode ? `/horz-values?groupCode=${groupCode}` : '/horz-values';
    return apiClient.get(url);
  },
  getById: (valueId) => apiClient.get(`/horz-values/${valueId}`),
  create: (data) => apiClient.post('/horz-values', data),
  update: (valueId, data) => apiClient.put(`/horz-values/${valueId}`, data),
  delete: (valueId) => apiClient.delete(`/horz-values/${valueId}`),
};

export const sampleOrdersAPI = {
  getOrderChain: (publicId) => apiClient.get(`/sample-orders/${publicId}/chain`),
  // All IDs in URLs are public_ids (UUIDs)
  getAll: (queryString = '') => {
    const url = queryString ? `/sample-orders?${queryString}` : '/sample-orders';
    return apiClient.get(url);
  },
  getById: (publicId) => apiClient.get(`/sample-orders/${publicId}`),
  create: (data) => apiClient.post('/sample-orders', data),
  update: (publicId, data) => apiClient.put(`/sample-orders/${publicId}`, data),
  delete: (publicId) => apiClient.delete(`/sample-orders/${publicId}`),
  getHorzValues: (groupCode) => apiClient.get(`/horz-values?groupCode=${groupCode}`),  // horz_value lookup by group
  createFollowUp: (publicId, data) => apiClient.post(`/sample-orders/${publicId}/follow-up`, data),
  getSampleOrderArticleList: (queryString) => apiClient.get(`/sample-orders/sample-order-articles?${queryString}`),
  downloadPdf: (publicId, lang = 'pt') => apiClient.get(
    `/sample-orders/${publicId}/pdf?lang=${lang}`,
    { responseType: 'arraybuffer' }
  ),

  downloadFullPdf: (publicId, lang = 'pt') => apiClient.get(
    `/sample-orders/${publicId}/full-pdf?lang=${lang}`,
    { responseType: 'arraybuffer' }
  ),  

  getForCalendar: (deliveryMonth) => apiClient.get(`/sample-orders?calendar=true&deliveryMonth=${deliveryMonth}`),

  emailComments: (publicId) => apiClient.post(`/sample-orders/${publicId}/email-comments`),

};

export const attachmentsAPI = {
  upload: (entityType, entityId, files, description = '', category = '', prefix = '') => {
    const formData = new FormData();
    formData.append('entityType', entityType);
    formData.append('entityId', entityId);
    if (description) formData.append('description', description);
    if (category) formData.append('category', category);

    if (prefix) {
      formData.append('prefix', prefix);  // Add prefix
    }    
    
    // Add all files
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    
    return apiClient.post('/attachments/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  getAttachments: (entityType, entityId, category = '') => {
    const params = new URLSearchParams({
      entityType,
      entityId: entityId.toString()
    });
    if (category) params.append('category', category);
    
    return apiClient.get(`/attachments?${params.toString()}`);
  },
  
  getAttachmentCounts: (entityType, entityIds) => {
    const params = new URLSearchParams({
      entityType,
      entityIds: entityIds.join(',')
    });
    
    return apiClient.get(`/attachments/counts?${params.toString()}`);
  },
  
  supersede: (attachmentId, newAttachmentId = null) =>
    apiClient.post(`/attachments/${attachmentId}/supersede`, { new_attachment_id: newAttachmentId }),
  
  download: (attachmentId) => {
    // Get token from localStorage
    const token = localStorage.getItem('accessToken');

    // Return URL with token as query parameter for direct download
    return `${apiClient.defaults.baseURL}/attachments/${attachmentId}/download?token=${token}`;
  },
  
  delete: (attachmentId) => {
    return apiClient.delete(`/attachments/${attachmentId}`);
  }
};


export const actionPlansAPI = {
  getAll:       (params) => apiClient.get(`/action-plans?${params}`),
  getById:      (id)     => apiClient.get(`/action-plans/${id}`),
  create:       (data)   => apiClient.post('/action-plans', data),
  update:       (id, data) => apiClient.put(`/action-plans/${id}`, data),
  delete:       (id)     => apiClient.delete(`/action-plans/${id}`),
  // Actions
  addAction:    (planId, data) => apiClient.post(`/action-plans/${planId}/actions`, data),
  updateAction: (actionId, data) => apiClient.put(`/action-plans/actions/${actionId}`, data),
  deleteAction: (actionId) => apiClient.delete(`/action-plans/actions/${actionId}`),
  // Updates (progress log)
  addUpdate:    (planId, data) => apiClient.post(`/action-plans/${planId}/updates`, data),
  // Members
  addMember:    (planId, data) => apiClient.post(`/action-plans/${planId}/members`, data),
  removeMember: (memberId) => apiClient.delete(`/action-plans/members/${memberId}`),
  // Dashboard
  getDashboard: () => apiClient.get('/action-plans/dashboard'),
  getAreas:     () => apiClient.get('/action-plans/areas'),
  downloadReport: (id) => apiClient.get(  `/action-plans/${id}/report`,  { responseType: 'arraybuffer' }),

  getTasksList:     (params) => apiClient.get(`/action-plans/tasks-list?${params}`),
  getTasksListPdf:  (params) => apiClient.get(`/action-plans/tasks-list/pdf?${params}`,
    { responseType: 'arraybuffer' }),
  getTasksListExcel:(params) => apiClient.get(`/action-plans/tasks-list/excel?${params}`,
    { responseType: 'arraybuffer' }),

    getActionById: (actionId) => apiClient.get(`/action-plans/actions/${actionId}`),

};

export const apLookupsAPI = {
  // Areas
  getAreas:      ()        => apiClient.get('/ap-lookups/areas'),
  createArea:    (data)    => apiClient.post('/ap-lookups/areas', data),
  updateArea:    (id, data)=> apiClient.put(`/ap-lookups/areas/${id}`, data),
  deleteArea:    (id)      => apiClient.delete(`/ap-lookups/areas/${id}`),
  // Sub-areas (?areaId=x)
  getSubAreas:   (areaId)  => apiClient.get(`/ap-lookups/sub-areas${areaId ? '?areaId='+areaId : ''}`),
  createSubArea: (data)    => apiClient.post('/ap-lookups/sub-areas', data),
  updateSubArea: (id, data)=> apiClient.put(`/ap-lookups/sub-areas/${id}`, data),
  deleteSubArea: (id)      => apiClient.delete(`/ap-lookups/sub-areas/${id}`),
  // Categories
  getCategories:   ()        => apiClient.get('/ap-lookups/categories'),
  createCategory:  (data)    => apiClient.post('/ap-lookups/categories', data),
  updateCategory:  (id, data)=> apiClient.put(`/ap-lookups/categories/${id}`, data),
  deleteCategory:  (id)      => apiClient.delete(`/ap-lookups/categories/${id}`),
};

export const settingsAPI = {
  getAll:    ()          => apiClient.get('/settings'),
  get:       (key)       => apiClient.get(`/settings/${key}`),
  update:    (key, value)=> apiClient.put(`/settings/${key}`, { value }),
};

export const cuttingPlansAPI = {
  // Session working data
  getOrders: (sessionIp) => apiClient.get(`/cutting-plans/orders?sessionIp=${sessionIp}`),
  loadOrders: (data) => apiClient.post('/cutting-plans/orders/load', data),
  toggleSelection: (id, isSelected) => apiClient.patch(`/cutting-plans/orders/${id}/selection`, { isSelected }),
  getSummary: (sessionIp) => apiClient.get(`/cutting-plans/summary?sessionIp=${sessionIp}`),
  
  // Saved plans
  loadOrdersFromErp:  (data) => apiClient.post('/cutting-plans/orders/load-erp', data),
  loadOrderDetails:   (data) => apiClient.post('/cutting-plans/orders/load-details', data),
  getAll: (params) => apiClient.get(`/cutting-plans/plans?${params}`),
  getById: (planId) => apiClient.get(`/cutting-plans/plans/${planId}`),
  create: (data) => apiClient.post('/cutting-plans/plans', data)
};


export const commentsAPI = {
  getComments: (entityType, entityId) => 
    apiClient.get(`/comments?entityType=${entityType}&entityId=${entityId}`),
  
  getUnreadCount: (entityType, entityId) => 
    apiClient.get(`/comments/unread-count?entityType=${entityType}&entityId=${entityId}`),
  
  getAvailableUsers: () => 
    apiClient.get('/comments/users'),
  
  create: (data) => 
    apiClient.post('/comments', data),
  
  markAsViewed: (commentId) => 
    apiClient.post(`/comments/${commentId}/view`),
  
  markAllAsViewed: (entityType, entityId) => 
    apiClient.post('/comments/view-all', { entityType, entityId }),

  getSubCounts: (entityType, entityId, subEntityType) =>
    apiClient.get('/comments/sub-counts', { params: { entityType, entityId, subEntityType } }),  

};


export const notificationsAPI = {
  getAll:          (params) => apiClient.get('/notifications', { params }),
  getUnreadCount:  ()       => apiClient.get('/notifications/unread-count'),
  markRead:        (id)     => apiClient.put(`/notifications/${id}/read`),
  markAllRead:     ()       => apiClient.put('/notifications/mark-all-read'),
  deleteNotification: (id) => apiClient.delete(`/notifications/${id}`),

  // Subscriptions with filters
  getSubscriptionsWithFilters: () => apiClient.get('/notifications/subscriptions'),
  ensureSubscription:  (data)        => apiClient.post('/notifications/subscriptions', data),
  updateSubscription:  (id, data)    => apiClient.put(`/notifications/subscriptions/${id}`, data),
  addFilter:           (subId, data) => apiClient.post(`/notifications/subscriptions/${subId}/filters`, data),
  updateFilter:        (filterId, data) => apiClient.put(`/notifications/filters/${filterId}`, data),
  deleteFilter:        (filterId)    => apiClient.delete(`/notifications/filters/${filterId}`),
  getFilterableFields: (entityType) => apiClient.get(`/notifications/filterable-fields/${entityType}`),
  // Admin endpoints
  adminGetUserSubscriptions:  (userId)         => apiClient.get(`/notifications/admin/users/${userId}/subscriptions`),
  adminEnsureSubscription:   (userId, data)   => apiClient.post(`/notifications/admin/users/${userId}/subscriptions`, data),
  adminUpdateSubscriptionFn:  (id, data)       => apiClient.put(`/notifications/admin/subscriptions/${id}`, data),
  addAdminFilter:             (subId, data)    => apiClient.post(`/notifications/admin/subscriptions/${subId}/filters`, data),
  adminUpdateFilter:          (filterId, data) => apiClient.put(`/notifications/admin/filters/${filterId}`, data),
  adminDeleteFilter:          (filterId)       => apiClient.delete(`/notifications/admin/filters/${filterId}`),
  copySubscriptions:          (fromUserId, toUserId) => apiClient.post('/notifications/admin/copy-subscriptions', { fromUserId, toUserId }),

  // Entity field definitions (admin)
  getAllEntityFields:    ()            => apiClient.get('/notifications/entity-fields'),
  getEntityFieldTypes:  ()            => apiClient.get('/notifications/entity-fields/types'),
  createEntityField:    (data)        => apiClient.post('/notifications/entity-fields', data),
  updateEntityField:    (id, data)    => apiClient.put(`/notifications/entity-fields/${id}`, data),
  deleteEntityField:    (id)          => apiClient.delete(`/notifications/entity-fields/${id}`),

};

// Add to existing exports
export const subscriptionsAPI = {
  // Notification subscriptions
  getNotificationSubscriptions: () => 
    apiClient.get('/subscriptions/notifications'),
  
  subscribe: (notificationTypeCode, entityType = null, notifyEmail = false) =>
    apiClient.post('/subscriptions/notifications/subscribe', {
      notificationTypeCode,
      entityType,
      notifyEmail
    }),
  
  unsubscribe: (notificationTypeCode, entityType = null) =>
    apiClient.post('/subscriptions/notifications/unsubscribe', {
      notificationTypeCode,
      entityType
    }),
  
  // Comment subscriptions
  getCommentSubscriptions: () =>
    apiClient.get('/subscriptions/comments'),
  
  subscribeToComments: (entityType, entityId = null, notifyEmail = false) =>
    apiClient.post('/subscriptions/comments/subscribe', {
      entityType,
      entityId,
      notifyEmail
    }),
  
  unsubscribeFromComments: (entityType, entityId = null) =>
    apiClient.post('/subscriptions/comments/unsubscribe', {
      entityType,
      entityId
    })
};


export const auditLogAPI = {
  getAuditLogs: (filters) => {
    const params = new URLSearchParams();
    if (filters.entityType) params.append('entityType', filters.entityType);
    if (filters.entityId) params.append('entityId', filters.entityId);
    if (filters.operationType) params.append('operationType', filters.operationType);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.changedBy) params.append('changedBy', filters.changedBy);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    
    return apiClient.get(`/audit-log?${params.toString()}`);
  },
  
  getAuditLogDetails: (auditLogId) =>
    apiClient.get(`/audit-log/${auditLogId}/details`),
  
  getAuditLogCount: (entityType, entityId = null) => {
    const params = new URLSearchParams();
    if (entityType) params.append('entityType', entityType);
    if (entityId) params.append('entityId', entityId);
    return apiClient.get(`/audit-log/count?${params.toString()}`);
  }
};


// Products API
export const productsAPI = {
  getAll: (queryString = '') => apiClient.get(`/products${queryString}`),
  getById: (publicId) => apiClient.get(`/products/${publicId}`),
  create: (data) => apiClient.post('/products', data),
  update: (publicId, data) => apiClient.put(`/products/${publicId}`, data),
  delete: (publicId) => apiClient.delete(`/products/${publicId}`)
};


export const areasAPI = {
  getAll:           ()               => apiClient.get('/areas'),
  create:           (data)           => apiClient.post('/areas', data),
  update:           (publicId, data) => apiClient.put(`/areas/${publicId}`, data),
  delete:           (publicId)       => apiClient.delete(`/areas/${publicId}`),

  getOperations:    (areaPublicId)          => apiClient.get(`/areas/${areaPublicId}/operations`),
  getAllOperations:  ()                      => apiClient.get('/areas/operations/all'),
  createOperation:  (areaPublicId, data)    => apiClient.post(`/areas/${areaPublicId}/operations`, data),
  updateOperation:  (publicId, data)        => apiClient.put(`/areas/operations/${publicId}`, data),
  deleteOperation:  (publicId)              => apiClient.delete(`/areas/operations/${publicId}`),

  getUsers:         (areaPublicId)          => apiClient.get(`/areas/${areaPublicId}/users`),
  addUser:          (areaPublicId, data)    => apiClient.post(`/areas/${areaPublicId}/users`, data),
  removeUser:       (areaPublicId, userId)  => apiClient.delete(`/areas/${areaPublicId}/users/${userId}`),

  getSampleTypeOps: (sampleOrderTypeId)     => apiClient.get(`/areas/sample-type/${sampleOrderTypeId}/operations`),
  upsertSampleTypeOp: (sampleOrderTypeId, data) => apiClient.post(`/areas/sample-type/${sampleOrderTypeId}/operations`, data),
  removeSampleTypeOp: (sampleOrderTypeId, operationId) => apiClient.delete(`/areas/sample-type/${sampleOrderTypeId}/operations/${operationId}`),
};

export const lineOperationsAPI = {
  // Available operations (checkbox source for LineOperationsModal)
  getAvailableOperations: (sampleOrderTypeId) =>
    apiClient.get(`/line-operations/available${sampleOrderTypeId ? `?sampleOrderTypeId=${sampleOrderTypeId}` : ''}`),

  // My tasks list
  getMyTasks: (queryString) =>
    apiClient.get(`/line-operations/my-tasks?${queryString}`),

  // Per-article main API (LineOperationsModal)
  // GET  /:orderPublicId/line/:matrixLineNo   optional ?cycle_no=N
  // POST /:orderPublicId/line/:matrixLineNo   body includes cycle_no
  getForArticle: (orderPublicId, matrixLineNo, cycleNo = null) =>
    apiClient.get(`/line-operations/${orderPublicId}/line/${matrixLineNo}`,
      { params: cycleNo != null ? { cycle_no: cycleNo } : {} }),

  saveArticle: (orderPublicId, matrixLineNo, data) =>
    apiClient.post(`/line-operations/${orderPublicId}/line/${matrixLineNo}`, data),

  getCycles: (orderPublicId, matrixLineNo) =>
    apiClient.get(`/line-operations/${orderPublicId}/line/${matrixLineNo}/cycles`),

  startNewCycle: (orderPublicId, matrixLineNo) =>
    apiClient.post(`/line-operations/${orderPublicId}/line/${matrixLineNo}/new-cycle`),

  // Single operation CRUD
  update: (publicId, data) => apiClient.put(`/line-operations/${publicId}`, data),
  delete: (publicId)       => apiClient.delete(`/line-operations/${publicId}`),

  // Order-level (all lines for a whole order)
  getForOrder: (orderPublicId) =>
    apiClient.get(`/line-operations/order/${orderPublicId}`),

  // Lookup helpers
  getAreas:          () => apiClient.get('/line-operations/areas'),
  getOperationTypes: () => apiClient.get('/line-operations/operation-types'),

  // Init planned dates
  initDates: (orderPublicId, matrixLineNo, data) =>
    apiClient.post(`/line-operations/for-order/${orderPublicId}/line/${matrixLineNo}/init-dates`, data),

  // Legacy aliases (kept for backward compat with any code still using old names)
  getLineOperations: (orderPublicId, matrixLineNo) =>
    apiClient.get(`/line-operations/order/${orderPublicId}/article/${matrixLineNo}`),
  saveArticleOperations: (orderPublicId, matrixLineNo, data) =>
    apiClient.post(`/line-operations/order/${orderPublicId}/article/${matrixLineNo}`, data),
  getForOrderLine: (orderPublicId, matrixLineNo) =>
    apiClient.get(`/line-operations/for-order/${orderPublicId}/line/${matrixLineNo}`),

    // Other operations of the same order + same operation + same cycle (other lines)
  getSiblings: (publicId) =>
    apiClient.get(`/line-operations/${publicId}/siblings`),

  // Copy the chosen fields of one operation to several sibling operations
  applyToOthers: (publicId, data) =>
    apiClient.post(`/line-operations/${publicId}/apply-to-others`, data),
};


export const ap_areasAPI = {
  getAll:        ()         => apiClient.get('/ap-areas'),
  getById:       (id)       => apiClient.get(`/ap-areas/${id}`),
  create:        (data)     => apiClient.post('/ap-areas', data),
  update:        (id, data) => apiClient.put(`/ap-areas/${id}`, data),
  delete:        (id)       => apiClient.delete(`/ap-areas/${id}`),
};

export const ap_subAreasAPI = {
  getAll:        (params)   => apiClient.get('/ap-sub-areas', { params }), // supports { area_id }
  getById:       (id)       => apiClient.get(`/ap-sub-areas/${id}`),
  create:        (data)     => apiClient.post('/ap-sub-areas', data),
  update:        (id, data) => apiClient.put(`/ap-sub-areas/${id}`, data),
  delete:        (id)       => apiClient.delete(`/ap-sub-areas/${id}`),
};

export const ap_categoriesAPI = {
  getAll:        ()         => apiClient.get('/ap-categories'),
  getById:       (id)       => apiClient.get(`/ap-categories/${id}`),
  create:        (data)     => apiClient.post('/ap-categories', data),
  update:        (id, data) => apiClient.put(`/ap-categories/${id}`, data),
  delete:        (id)       => apiClient.delete(`/ap-categories/${id}`),
};


export const fastReactAPI = {
  // Step 1: upload file → get jobId + total rows
  uploadForImport: (formData) =>
    apiClient.post('/fastreact/setup-fios/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // Step 2: SSE stream URL is opened directly with EventSource (not axios)
  // See ImportSetupFios.jsx for usage
 
  // Legacy non-streaming (fallback)
  importSetupFios: (formData) =>
    apiClient.post('/fastreact/setup-fios/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  previewSetupFios: () =>
    apiClient.get('/fastreact/setup-fios/preview'),
 
  // Setup Fios from Navision
  // runSetupFiosNavision returns a URL (opened as EventSource, not axios)
  getSetupFiosNavisionUrl: () => {
    const token = localStorage.getItem('accessToken') || '';
    return `${apiClient.defaults.baseURL}/fastreact/setup-fios-navision/run?token=${encodeURIComponent(token)}`;
  },
  previewSetupFiosNavision: () =>
    apiClient.get('/fastreact/setup-fios-navision/preview'),

  
  getPoSetups: (includeCompleted = false) =>
    apiClient.get('/fastreact/setup-fios-navision/po-setups', {
      params: { include_completed: includeCompleted },
    }),
 
  getPreparationItems: (poSetupId) =>
    apiClient.get(`/fastreact/setup-fios-navision/po-setups/${poSetupId}/items`),
 
  advancePreparationStatus: (preparationId, containerMaterial = null) =>
    apiClient.post(`/fastreact/setup-fios-navision/preparation/${preparationId}/advance`,
      { container_material: containerMaterial }),

  revertPreparationStatus: (preparationId) =>
    apiClient.post(`/fastreact/setup-fios-navision/preparation/${preparationId}/revert`),

  markRestock: (preparationId, noRestockCones) =>
    apiClient.post(`/fastreact/setup-fios-navision/preparation/${preparationId}/restock`,
      { num_restock_cones: noRestockCones }),

  toggleUrgent: (poSetupId) =>
    apiClient.patch(`/fastreact/plano-teares/${poSetupId}/urgent`),

  updateContainerMaterial: (poSetupId, containerMaterial) =>
    apiClient.patch(`/fastreact/plano-teares/${poSetupId}/container-material`,
      { container_material: containerMaterial }),

  updatePoSetupStatus: (poSetupId, status) =>
    apiClient.patch(`/fastreact/plano-teares/${poSetupId}/status`, { status }),

  pickupContainer: (poCodeLineFull) =>
    apiClient.post('/fastreact/plano-teares/pickup', { po_code_line_full: poCodeLineFull }),

  // Web: streams PDF directly → open in new browser tab
  streamPrepLabel: (poSetupId) =>
    apiClient.get(`/fastreact/plano-teares/${poSetupId}/label-pdf`, { responseType: 'arraybuffer' }),

  // Mobile/agent: stores in print_queue for local agent pickup
  queuePrintLabel: (poSetupId, printerName = null) =>
    apiClient.post(`/fastreact/plano-teares/${poSetupId}/print-label`, { printer_name: printerName }),

  getPlanoTeares: (params = {}) =>
    apiClient.get('/fastreact/plano-teares', { params }),

  getPlanoTearesItem: (poSetupId) =>
    apiClient.get(`/fastreact/plano-teares/${poSetupId}`),

  getRestockGroups: (params = {}) => apiClient.get('/fastreact/restock/groups', { params }),
  getRestockGroupItems: (queryString) => apiClient.get(`/fastreact/restock/groups/items?${queryString}`),

  getDailyRequirements: (params = {}) => apiClient.get('/fastreact/daily-requirements', { params }),

  // Corrections to a restock marked by mistake (mobile Reabastecer):
  updateRestockCones: (preparationId, numRestockCones) =>
    apiClient.patch(`/fastreact/setup-fios-navision/preparation/${preparationId}/restock-cones`, { num_restock_cones: numRestockCones }),
  clearRestock: (preparationId) =>
    apiClient.post(`/fastreact/setup-fios-navision/preparation/${preparationId}/restock/clear`),

  
};


export const powerbiAPI = {
  // Browse PBI workspace/reports/pages
  getWorkspaces:       ()               => apiClient.get('/powerbi/workspaces'),
  getWorkspaceReports: (groupId)        => apiClient.get(`/powerbi/workspaces/${groupId}/reports`),
  getReportPages:      (groupId, repId) => apiClient.get(`/powerbi/workspaces/${groupId}/reports/${repId}/pages`),
 
  // Subscriptions CRUD
  listSubscriptions:   ()         => apiClient.get('/powerbi/subscriptions'),
  getSubscription:     (id)       => apiClient.get(`/powerbi/subscriptions/${id}`),
  createSubscription:  (data)     => apiClient.post('/powerbi/subscriptions', data),
  updateSubscription:  (id, data) => apiClient.put(`/powerbi/subscriptions/${id}`, data),
  deleteSubscription:  (id)       => apiClient.delete(`/powerbi/subscriptions/${id}`),
 
  // Run now + history
  runNow:      (id) => apiClient.post(`/powerbi/subscriptions/${id}/run`),
  getRunHistory:(id) => apiClient.get(`/powerbi/subscriptions/${id}/history`),

  // Auth
  getAuthStatus:   ()     => apiClient.get('/powerbi/auth/status'),
  initDeviceAuth:  ()     => apiClient.post('/powerbi/auth/init'),
  pollDeviceAuth:  (data) => apiClient.post('/powerbi/auth/poll', data),
};


export const ediAPI = {
  getClients: () => apiClient.get('/edi/clients'),
  createClient: (data) => apiClient.post('/edi/clients', data),
  updateClient: (id, data) => apiClient.put(`/edi/clients/${id}`, data),
  deleteClient: (id) => apiClient.delete(`/edi/clients/${id}`),
  retrieveNow: (id) => apiClient.post(`/edi/clients/${id}/retrieve-now`),

  // Client GLNs / stores
  getClientGlns: (clientId) => apiClient.get(`/edi/clients/${clientId}/glns`),
  createClientGln: (clientId, data) => apiClient.post(`/edi/clients/${clientId}/glns`, data),
  updateClientGln: (id, data) => apiClient.put(`/edi/glns/${id}`, data),
  deleteClientGln: (id) => apiClient.delete(`/edi/glns/${id}`),

  getMessages: (queryString = '') => {
    const url = queryString ? `/edi/messages?${queryString}` : '/edi/messages';
    return apiClient.get(url);
  },

  // Excel (.xlsx) download for ONE log record (processed file) — must be a blob
  exportMessage: (id) =>
    apiClient.get(`/edi/messages/${id}/export`, { responseType: 'blob' }),
};



export const quantixgoAPI = {
  exportProductsFromErp:     () => apiClient.post('/quantixgo/nav-export/products'),
  exportSalespersonsFromErp: () => apiClient.post('/quantixgo/nav-export/salespersons'),
  exportSalesPricesFromErp:  () => apiClient.post('/quantixgo/nav-export/sales-prices'),
  exportProductVariantsFromErp: () => apiClient.post('/quantixgo/nav-export/product-variants'),
  exportCustomersFromErp: () => apiClient.post('/quantixgo/nav-export/customers'),
  exportCustomerSalespersonsFromErp: () => apiClient.post('/quantixgo/nav-export/customer-salespersons'),  
  exportDeliveryAddressesFromErp: () => apiClient.post('/quantixgo/nav-export/delivery-addresses'),
  importProductImages: () => apiClient.post('/quantixgo/images/import'),
  getImageImportLog: (limit) => apiClient.get(`/quantixgo/images/import-log${limit ? `?limit=${limit}` : ''}`),
  getImageImportLogDetail: (id) => apiClient.get(`/quantixgo/images/import-log/${id}`),
  listDeviceRequests: (status) => apiClient.get(`/quantixgo/devices/requests${status ? `?status=${status}` : ''}`),
  getDeviceApprovalLookups: () => apiClient.get('/quantixgo/devices/lookups'),
  approveDeviceRequest: (requestId, payload) => apiClient.post(`/quantixgo/devices/requests/${requestId}/approve`, payload),
  searchImages: (search) => apiClient.get(`/quantixgo/images${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  deleteProductImages: (productCode) => apiClient.delete(`/quantixgo/images/${encodeURIComponent(productCode)}`),  
  listActiveDevices: () => apiClient.get('/quantixgo/devices/active'),
  unassignDevice: (deviceId) => apiClient.post(`/quantixgo/devices/${deviceId}/unassign`),  

  listTransportCosts: () => apiClient.get('/quantixgo/transport-costs'),
  createTransportCost: (payload) => apiClient.post('/quantixgo/transport-costs', payload),
  updateTransportCost: (id, payload) => apiClient.put(`/quantixgo/transport-costs/${id}`, payload),
  deleteTransportCost: (id) => apiClient.delete(`/quantixgo/transport-costs/${id}`),

};

export default apiClient;